import Image from 'next/image'
import cx from 'classnames'
import styles from './HeroV2.module.scss'
import { NEXT_IMAGE_QUALITY } from '~constants'
import { Button, Heading } from '~elements'
import Video from 'components/elements/Video/Video'
import { ButtonProps, ResponsiveMultimedia } from '~types'
import breakpoints from '../../../styles/breakpoints.json'
import { useWindowSize } from 'utils/hooks'
import { getGtmLinkPosition } from '~analyticsUtils'

export type HeroV2Props = {
  alignHeadline?: 'center' | 'bottom'
  description?: string
  responsiveMultimedia?: ResponsiveMultimedia
  subtitle?: string
  title?: string
  showTitle?: boolean
  justify?: 'center' | 'left' | 'right'
  ctaButtons?: ButtonProps[]
  isChildOfHeroCarousel?: boolean
  heroHeight?: 'spacious' | 'compact'
}

const HeroV2 = ({
  alignHeadline,
  description,
  title,
  subtitle,
  responsiveMultimedia,
  ctaButtons,
  justify = 'left',
  showTitle,
  isChildOfHeroCarousel = false,
  heroHeight = 'spacious'
}: HeroV2Props) => {
  const linkLocation = isChildOfHeroCarousel ? 'hero_carousel' : ''
  const linkModule = 'hero'
  const { isBreakpoint: isBelowXs } = useWindowSize('xs', '<')
  const showHeadline = !!showTitle || !!subtitle || !!description || !!(ctaButtons && ctaButtons.length > 0)
  const hasLowerCta = !showTitle && !subtitle && !description && !!ctaButtons && ctaButtons.length > 0
  const lowerHeadline = hasLowerCta || alignHeadline === 'bottom'
  const hasTwoButtons = ctaButtons && ctaButtons.length > 1
  const {
    posterImageSm,
    posterImageMd,
    posterImageXl,
    backgroundVideoSm,
    backgroundVideoMd,
    backgroundVideoXl,
    allowPause,
    allowVolume
  } = responsiveMultimedia ?? {}
  const fallbackImage = posterImageSm ?? posterImageMd ?? posterImageXl
  const fallbackAlt = posterImageSm?.altText ?? posterImageMd?.altText ?? posterImageXl?.altText ?? title ?? ''

  const hasVideo = Boolean(backgroundVideoXl ?? backgroundVideoMd ?? backgroundVideoSm)

  return (
    <div
      className={cx(
        styles['hero-container'],
        title && !showTitle && styles['hero-container__hidden-title'],
        heroHeight === 'compact' && styles['hasCompactHeight'],
        (hasVideo || fallbackImage) && styles['hasBgGradient']
      )}
    >
      <div className={styles['hero__media-wrapper']}>
        {hasVideo ? (
          <Video
            backgroundVideoXl={backgroundVideoXl}
            backgroundVideoMd={backgroundVideoMd}
            backgroundVideoSm={backgroundVideoSm}
            posterImageXl={posterImageXl}
            posterImageMd={posterImageMd}
            posterImageSm={posterImageSm}
            autoPlay={true}
            videoLoop={Boolean(responsiveMultimedia?.videoLoop)}
            allowPause={allowPause}
            allowVolume={allowVolume}
            isChildOfHeroCarousel
          />
        ) : (
          responsiveMultimedia && (
            <picture>
              {posterImageXl?.url && <source srcSet={posterImageXl.url} media={`(min-width: ${breakpoints.lg}px)`} />}
              {posterImageMd?.url && <source srcSet={posterImageMd.url} media={`(min-width: ${breakpoints.sm}px)`} />}
              {fallbackImage?.url && (
                <Image
                  src={fallbackImage?.url}
                  alt={fallbackAlt}
                  sizes='100vw'
                  fill
                  width={0}
                  height={0}
                  style={{
                    width: '100%',
                    height: '100%'
                  }}
                  quality={NEXT_IMAGE_QUALITY}
                  priority
                />
              )}
            </picture>
          )
        )}
      </div>

      {title && !showTitle && (
        <Heading level={1} justify='left' showHeading={showTitle}>
          {title}
        </Heading>
      )}

      {showHeadline && (
        <div
          className={cx(
            styles['headline-container'],
            justify === 'center' && styles['headline-center'],
            lowerHeadline && styles['headline-container__lower'],
            hasLowerCta && styles['headline-container__lower-cta-only']
          )}
        >
          {subtitle && (
            <Heading level={4} justify='left'>
              {subtitle}
            </Heading>
          )}

          {title && showTitle && (
            <Heading level={1} justify='left'>
              {title}
            </Heading>
          )}

          {description && (
            <Heading level={5} justify='left'>
              {description}
            </Heading>
          )}

          {ctaButtons && ctaButtons.length > 0 && (
            <div
              className={`${styles['btn-container']} ${hasTwoButtons ? styles['btn-container--has-two-buttons'] : ''}`}
            >
              {ctaButtons.map((button, index) => (
                <Button
                  key={`${button.title}_${index}`}
                  {...button}
                  {...(isBelowXs &&
                    hasTwoButtons && {
                      size: 'md',
                      isFullWidth: true
                    })}
                  analytics={{
                    linkModule: linkModule,
                    clickTracking: `${linkModule}:${title}:${button.title}:cta`,
                    linkPosition: getGtmLinkPosition(ctaButtons, index),
                    linkLocation
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default HeroV2
